import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import TablePagination from '@mui/material/TablePagination';
import TextField from "@mui/material/TextField";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import InboxIcon from '@mui/icons-material/Inbox';
import StarIcon from '@mui/icons-material/Star';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import useHRCopy from "../hr/i18n/useHRCopy";


export default function DashboardTable() {
  const d = useHRCopy().dashboard;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRow, setExpandedRow] = useState(null);
  const [username, setUsername] = useState("");
  const [filters, setFilters] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("user-service"); // default to one
  const [requests, setRequests] = useState({
      "user-service": [],
      "employer-service": [],
      "candidate-service": [],
  });

  useEffect(() => {
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          const decoded = jwtDecode(token); // jwtDecode from the named export
          setUsername(decoded.username);
        } catch (err) {
          console.error("Invalid token:", err);
        }
      }
  }, []);

  // Fetch each service separately and store in keyed state
  useEffect(() => {
      async function fetchService(name, url) {
      const res = await fetch(url);
      const data = await res.json();
      return data;
  }

  async function fetchAll() {
    try {
      const [userData, employerData, candidateData] = await Promise.all([
        // fetchService("user-service", "http://localhost:5001/api/contacts"),
        // fetchService("employer-service", "http://localhost:5003/api/employers"),
        // fetchService("candidate-service", "http://localhost:5002/api/candidates"),
        fetchService("user-service",`${process.env.REACT_APP_API_URL_USER}/contacts`),
        fetchService("employer-service", `${process.env.REACT_APP_API_URL_EMPLOYER}/employers`),
        fetchService("candidate-service", `${process.env.REACT_APP_API_URL_CANDIDATE}/candidates`)
      ]);

      setRequests({
        "user-service": userData,
        "employer-service": employerData,
        "candidate-service": candidateData,
      });
    } catch (err) {
      console.error("Error fetching service data:", err);
    }
  }
  fetchAll();
  }, []);

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Define headers for each service
  const tableHeaders = {
    "user-service": d.columns.user,
    "employer-service": d.columns.employer,
    "candidate-service": d.columns.candidate,
  };

  // Render each row differently depending on service
  const renderRow = (item) => {
    switch (selectedService) {
      case "user-service":
        return (
          <>
            <td className="py-3 px-4">{item.name}</td>
            <td className="py-3 px-4">{item.email}</td>
            <td className="py-3 px-4">{item.message}</td>
            <td className="py-3 px-4">{new Date(item.createdAt).toLocaleString()}</td>
          </>
        );
      case "employer-service":
        return (
          <>
            <td className="py-3 px-4">{item.service}</td>
            <td className="py-3 px-4">{item.message}</td>
            <td className="py-3 px-4">{new Date(item.createdAt).toLocaleString()}</td>
          </>
        );
      case "candidate-service":
        return (
          <>
            <td className="py-3 px-4">{item.fullName}</td>
            <td className="py-3 px-4">{item.email}</td>
            <td className="py-3 px-4">{item.phone}</td>
            <td className="py-3 px-4">{item.education}</td>
            <td className="py-3 px-4">{item.skills?.join(", ")}</td>
            <td className="py-3 px-4">{new Date(item.createdAt).toLocaleString()}</td>
          </>
        );
      default:
        return null;
    }
  };

  const currentRequests = requests[selectedService] || [];

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // reset to first page when filtering
  };
  
  // filter first
  const filteredRequests = currentRequests.filter((req) => {
    return Object.entries(filters).every(([field, value]) => {
      if (!value) return true; // no filter on this field
  
      const fieldValue = req[field];
      if (field === "createdAt") {
        if (!fieldValue) return false;
        // format createdAt to YYYY-MM-DD
        const dateOnly = new Date(fieldValue).toISOString().slice(0, 10);
        return dateOnly === value;
      }
  
      if (fieldValue == null) return false;
      return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
    });
  });
  

  // then paginate the filtered results
  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );  

  const renderCategoryChanger = (item) => {
    const categories = ["inbox", "important", "archived", "trash"];
  
    return (
      <div className="flex space-x-1">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryUpdate(item._id, cat)}
            className={`p-1 rounded hover:bg-gray-100 ${
              item.category === cat ? "bg-gray-200" : ""
            }`}
            title={cat}
          >
            {categoryIcons[cat]}
          </button>
        ))}
      </div>
    );
  };

  const handleCategoryUpdate = async (id, newCategory) => {
    try {
      const urlMap = {
        "user-service": `${process.env.REACT_APP_API_URL_USER}/contact/${id}/category`,
        "employer-service": `${process.env.REACT_APP_API_URL_EMPLOYER}/employer/${id}/category`,
        "candidate-service": `${process.env.REACT_APP_API_URL_CANDIDATE}/candidate/${id}/category`,
      };

      const res = await fetch(urlMap[selectedService], {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ categoryType: newCategory }),
      });
  
      const updated = await res.json();
  
      setRequests((prev) => ({
        ...prev,
        [selectedService]: prev[selectedService].map((item) =>
          item._id === id ? updated : item
        ),
      }));
    } catch (err) {
      console.error("Error updating category:", err);
    }
  };

  const categoryIcons = {
    inbox: <InboxIcon className="text-gray-500" />,
    important: <StarIcon className="text-yellow-500" />,
    archived: <ArchiveIcon className="text-blue-500" />,
    trash: <DeleteIcon className="text-red-500" />,
  };

  return (
    <div className="flex h-[calc(100vh-5rem)] bg-paper font-hanken">
      {/* Sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 sm:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`
          fixed sm:static top-0 left-0 h-full sm:h-auto bg-white shadow-md p-4 flex flex-col
          transform transition-transform duration-300 ease-in-out z-50
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          sm:translate-x-0
          w-64
        `}
      >
        <h2 className="text-xl font-bold mb-4">{d.title}</h2>
        <button
          onClick={() => {
            setSelectedService("employer-service");
            setSidebarOpen(false); // close on mobile after click
          }}
          className={`text-left p-2 rounded-md mb-2 ${
            selectedService === "employer-service" ? "bg-royal/10 text-navy font-semibold" : ""
          }`}
        >
          {d.tabs.employer}
        </button>
        <button
          onClick={() => {
            setSelectedService("candidate-service");
            setSidebarOpen(false);
          }}
          className={`text-left p-2 rounded-md mb-2 ${
            selectedService === "candidate-service" ? "bg-royal/10 text-navy font-semibold" : ""
          }`}
        >
          {d.tabs.candidate}
        </button>
        <button
          onClick={() => {
            setSelectedService("user-service");
            setSidebarOpen(false);
          }}
          className={`text-left p-2 rounded-md mb-2 ${
            selectedService === "user-service" ? "bg-royal/10 text-navy font-semibold" : ""
          }`}
        >
          {d.tabs.user}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
      <header className="bg-white shadow-sm p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button
            className="sm:hidden p-2 rounded hover:bg-gray-100"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
          <h1 className="text-2xl font-semibold">{d.panel}</h1>
        </div>
        <div className="text-gray-600 text-sm">{d.user}: {username}</div>
      </header>

        <main className="p-6 overflow-auto">
          
          <h2 className="text-lg font-bold mb-4">
            {selectedService === "user-service" && d.tabs.user}
            {selectedService === "employer-service" && d.tabs.employer}
            {selectedService === "candidate-service" && d.tabs.candidate}
          </h2>

          <div className="bg-white shadow rounded-lg overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {tableHeaders[selectedService].map((header) => (
                      <th key={header} className="text-left py-3 px-4">
                        {header}
                      </th>
                    ))}
                    <th className="text-left py-3 px-4">{d.category}</th>
                    <th className="text-center py-3 px-4">{d.detail}</th>
                  </tr>
                  {/* Desktop Filter row */}
                  {selectedService === "user-service" && (
                    <tr>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.name}
                          variant="outlined"
                          size="small"
                          value={filters.name || ""}
                          onChange={(e) => handleFilterChange("name", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.email}
                          variant="outlined"
                          size="small"
                          value={filters.email || ""}
                          onChange={(e) => handleFilterChange("email", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.message}
                          variant="outlined"
                          size="small"
                          value={filters.message || ""}
                          onChange={(e) => handleFilterChange("message", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.createdAt}
                          type="date"
                          variant="outlined"
                          size="small"
                          value={filters.createdAt || ""}
                          onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          select
                          variant="outlined"
                          size="small"
                          value={filters.category || ""}
                          onChange={(e) => handleFilterChange("category", e.target.value)}
                          fullWidth
                          SelectProps={{ native: true }}
                        >
                          <option value="">{d.all}</option>
                          <option value="inbox">Inbox</option>
                          <option value="important">Important</option>
                          <option value="archived">Archived</option>
                          <option value="trash">Trash</option>
                        </TextField>
                      </th>
                    </tr>
                  )}

                  {selectedService === "employer-service" && (
                    <tr>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.service}
                          variant="outlined"
                          size="small"
                          value={filters.service || ""}
                          onChange={(e) => handleFilterChange("service", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.message}
                          variant="outlined"
                          size="small"
                          value={filters.message || ""}
                          onChange={(e) => handleFilterChange("message", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.createdAt}
                          type="date"
                          variant="outlined"
                          size="small"
                          value={filters.createdAt || ""}
                          onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          select
                          variant="outlined"
                          size="small"
                          value={filters.category || ""}
                          onChange={(e) => handleFilterChange("category", e.target.value)}
                          fullWidth
                          SelectProps={{ native: true }}
                        >
                          <option value="">{d.all}</option>
                          <option value="inbox">Inbox</option>
                          <option value="important">Important</option>
                          <option value="archived">Archived</option>
                          <option value="trash">Trash</option>
                        </TextField>
                      </th>
                    </tr>
                  )}

                  {selectedService === "candidate-service" && (
                    <tr>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.fullName}
                          variant="outlined"
                          size="small"
                          value={filters.fullName || ""}
                          onChange={(e) => handleFilterChange("fullName", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.email}
                          variant="outlined"
                          size="small"
                          value={filters.email || ""}
                          onChange={(e) => handleFilterChange("email", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.phone}
                          variant="outlined"
                          size="small"
                          value={filters.phone || ""}
                          onChange={(e) => handleFilterChange("phone", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.education}
                          variant="outlined"
                          size="small"
                          value={filters.education || ""}
                          onChange={(e) => handleFilterChange("education", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.skills}
                          variant="outlined"
                          size="small"
                          value={filters.skills || ""}
                          onChange={(e) => handleFilterChange("skills", e.target.value)}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          label={d.fields.createdAt}
                          type="date"
                          variant="outlined"
                          size="small"
                          value={filters.createdAt || ""}
                          onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </th>
                      <th className="px-4 py-2">
                        <TextField
                          select
                          variant="outlined"
                          size="small"
                          value={filters.category || ""}
                          onChange={(e) => handleFilterChange("category", e.target.value)}
                          fullWidth
                          SelectProps={{ native: true }}
                        >
                          <option value="">{d.all}</option>
                          <option value="inbox">Inbox</option>
                          <option value="important">Important</option>
                          <option value="archived">Archived</option>
                          <option value="trash">Trash</option>
                        </TextField>
                      </th>
                    </tr>
                  )}
                </thead>

                <tbody>
                  {paginatedRequests.length > 0 ? (
                    paginatedRequests.map((req) => (
                      <React.Fragment key={req._id}>
                        {/* Main Row */}
                        <tr
                          className={`border-b hover:bg-gray-100 ${
                            req.category === "important"
                              ? "bg-green-100"
                              : req.category === "trash"
                              ? "bg-red-100"
                              : req.category === "archived"
                              ? "bg-orange-100"
                              : ""
                          }`}
                        >
                          {renderRow(req)}
                          <td className="py-3 px-4">{renderCategoryChanger(req)}</td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => toggleRow(req._id)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {expandedRow === req._id ? "▲" : "▼"}
                            </button>
                          </td>
                        </tr>

                        {/* Details Row */}
                        {expandedRow === req._id && (
                          <tr className="bg-gray-50">
                            <td
                              colSpan={tableHeaders[selectedService].length + 1}
                              className="p-4 text-sm text-gray-700"
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {Object.entries(req)
                                  .filter(([key]) => key !== "__v")
                                  .map(([key, value]) => {
                                    let displayValue;
                                    if (Array.isArray(value)) {
                                      displayValue = value.join(", ");
                                    } else if (typeof value === "object" && value !== null) {
                                      displayValue = JSON.stringify(value, null, 2);
                                    } else {
                                      displayValue = value?.toString() || "";
                                    }
                                    return (
                                      <div
                                        key={key}
                                        className="flex flex-col border-b pb-2"
                                        style={{ wordBreak: "break-word" }}
                                      >
                                        <span className="font-semibold capitalize">{key}</span>
                                        <div className="max-h-48 overflow-auto p-2 bg-white rounded">
                                          {displayValue}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="py-3 px-4 text-center text-gray-500"
                        colSpan={tableHeaders[selectedService].length + 1}
                      >
                        {d.noData}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <TablePagination
                component="div"
                count={currentRequests.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage={d.rowsPerPage}
              />
            </div>

            {/* Mobile Filters */}
            <div className="sm:hidden flex flex-col space-y-4">
            <div className="sm:hidden mb-4 bg-white p-4 rounded-lg shadow space-y-3">
              {selectedService === "user-service" && (
                <>
                  <TextField
                    label={d.fields.name}
                    variant="outlined"
                    size="small"
                    value={filters.name || ""}
                    onChange={(e) => handleFilterChange("name", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.email}
                    variant="outlined"
                    size="small"
                    value={filters.email || ""}
                    onChange={(e) => handleFilterChange("email", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.message}
                    variant="outlined"
                    size="small"
                    value={filters.message || ""}
                    onChange={(e) => handleFilterChange("message", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.createdAt}
                    type="date"
                    variant="outlined"
                    size="small"
                    value={filters.createdAt || ""}
                    onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}

              {selectedService === "employer-service" && (
                <>
                  <TextField
                    label={d.fields.service}
                    variant="outlined"
                    size="small"
                    value={filters.service || ""}
                    onChange={(e) => handleFilterChange("service", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.message}
                    variant="outlined"
                    size="small"
                    value={filters.message || ""}
                    onChange={(e) => handleFilterChange("message", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.createdAt}
                    type="date"
                    variant="outlined"
                    size="small"
                    value={filters.createdAt || ""}
                    onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}

              {selectedService === "candidate-service" && (
                <>
                  <TextField
                    label={d.fields.fullName}
                    variant="outlined"
                    size="small"
                    value={filters.fullName || ""}
                    onChange={(e) => handleFilterChange("fullName", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.email}
                    variant="outlined"
                    size="small"
                    value={filters.email || ""}
                    onChange={(e) => handleFilterChange("email", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.phone}
                    variant="outlined"
                    size="small"
                    value={filters.phone || ""}
                    onChange={(e) => handleFilterChange("phone", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.education}
                    variant="outlined"
                    size="small"
                    value={filters.education || ""}
                    onChange={(e) => handleFilterChange("education", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.skills}
                    variant="outlined"
                    size="small"
                    value={filters.skills || ""}
                    onChange={(e) => handleFilterChange("skills", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={d.fields.createdAt}
                    type="date"
                    variant="outlined"
                    size="small"
                    value={filters.createdAt || ""}
                    onChange={(e) => handleFilterChange("createdAt", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}
            </div>
              {/* Mobile Card Layout */}
              <div className="sm:hidden flex flex-col space-y-4">
                {paginatedRequests.map((req) => (
                    <div
                      key={req._id}
                      className={`shadow rounded-lg p-4 space-y-2 ${
                        req.category === "important"
                          ? "bg-green-100"
                          : req.category === "trash"
                          ? "bg-red-100"
                          : req.category === "archived"
                          ? "bg-orange-100"
                          : "bg-white"
                      }`}
                    >
                    <div className="font-semibold text-base">{req.service || req.name || req.fullName}</div>
                    {req.message && (
                      <div className="text-gray-700 text-sm line-clamp-2">{req.message}</div>
                    )}
                    <div className="text-gray-500 text-xs">{new Date(req.createdAt).toLocaleString()}</div>
                    <button
                      onClick={() => toggleRow(req._id)}
                      className="text-blue-600 text-sm font-medium mt-2"
                    >
                      {expandedRow === req._id ? d.closeDetails : d.openDetails}
                    </button>
                    <td className="py-3">{renderCategoryChanger(req)}</td>
                    {expandedRow === req._id && (
                      <div className="mt-3 bg-gray-50 p-2 rounded-lg text-sm space-y-1 border">
                        {Object.entries(req)
                          .filter(([key]) => key !== "__v")
                          .map(([key, value]) => {
                            let displayValue;
                            if (Array.isArray(value)) displayValue = value.join(", ");
                            else if (typeof value === "object" && value !== null) displayValue = JSON.stringify(value, null, 2);
                            else displayValue = value?.toString() || "";
                            return (
                              <div key={key} className="flex justify-between border-b pb-1">
                                <span className="font-medium">{key}</span>
                                <span className="text-right break-all">{displayValue}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                ))}
                </div>

                <TablePagination
                  component="div"
                  count={filteredRequests.length}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage={d.rowsPerPage}
                />
              </div>
          </div>
        </main>
      </div>
    </div>
  );
}
